/**
 * Authentication context — wraps the app with Supabase session state.
 * Provides user info, current org, MFA status, and auth methods to all components.
 *
 * IMPORTANT: No Supabase MFA API calls (listFactors, getAuthenticatorAssuranceLevel)
 * may be made inside onAuthStateChange — they compete for the internal session lock
 * and cause deadlocks. AAL is read from the JWT; enrollment is checked separately.
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { api, setAccessToken } from '../../lib/api'

const AuthContext = createContext(null)

/** Decode JWT payload without verification (client-side, just to read claims). */
function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1]
    return JSON.parse(atob(base64))
  } catch {
    return {}
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [orgs, setOrgs] = useState([])
  const [currentOrg, setCurrentOrg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [orgError, setOrgError] = useState(null)

  // MFA state
  const [mfaEnrolled, setMfaEnrolled] = useState(false)
  const [mfaVerified, setMfaVerified] = useState(false)
  const [mfaChecked, setMfaChecked] = useState(false)

  // Track whether we need an enrollment check (set by onAuthStateChange, consumed by effect)
  const [needsEnrollmentCheck, setNeedsEnrollmentCheck] = useState(false)

  const timeoutRef = useRef(null)

  const loadOrgs = useCallback(async () => {
    setOrgError(null)
    try {
      const data = await api.get('/auth/me')
      setOrgs(data.orgs || [])

      const savedOrgId = localStorage.getItem('currentOrgId')
      const savedOrg = data.orgs?.find(o => o.id === savedOrgId)
      setCurrentOrg(savedOrg || data.orgs?.[0] || null)
    } catch (e) {
      console.error('Failed to load orgs:', e)
      if (e.message?.includes('Invalid token') || e.message?.includes('Not authenticated')) {
        await supabase.auth.signOut()
        setAccessToken(null)
        setUser(null)
        setOrgs([])
        setCurrentOrg(null)
        setOrgError(null)
        setLoading(false)
        return
      }
      setOrgError(e.message || 'Failed to load organization data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setLoading(false)
    }, 5000)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        clearTimeout(timeoutRef.current)

        if (session?.user) {
          setAccessToken(session.access_token)
          setUser(session.user)

          // Read AAL directly from the JWT — NO Supabase MFA API calls here.
          const payload = decodeJwtPayload(session.access_token)
          const aal = payload.aal || 'aal1'

          if (aal === 'aal2') {
            // Session is fully MFA-verified — user must be enrolled
            setMfaEnrolled(true)
            setMfaVerified(true)
            setMfaChecked(true)
            await loadOrgs()
          } else {
            // AAL1 — user either hasn't enrolled or hasn't verified yet.
            // Don't call MFA APIs here; flag for the separate effect below.
            setMfaVerified(false)
            setNeedsEnrollmentCheck(true)
            setLoading(false)
          }
        } else {
          setAccessToken(null)
          setUser(null)
          setOrgs([])
          setCurrentOrg(null)
          setOrgError(null)
          setMfaEnrolled(false)
          setMfaVerified(false)
          setMfaChecked(false)
          setNeedsEnrollmentCheck(false)
          setLoading(false)
        }
      }
    )

    return () => {
      clearTimeout(timeoutRef.current)
      subscription.unsubscribe()
    }
  }, [loadOrgs])

  // Separate effect to check enrollment — runs OUTSIDE of onAuthStateChange
  // so it doesn't compete for the Supabase session lock.
  useEffect(() => {
    if (!needsEnrollmentCheck) return

    let cancelled = false

    async function checkEnrollment() {
      try {
        const { data, error } = await supabase.auth.mfa.listFactors()
        if (cancelled) return
        if (error) {
          console.error('MFA enrollment check failed:', error)
          setMfaEnrolled(false)
        } else {
          const hasVerifiedTotp = data.totp?.some(f => f.status === 'verified')
          setMfaEnrolled(!!hasVerifiedTotp)
        }
      } catch (err) {
        if (cancelled) return
        console.error('MFA enrollment check error:', err)
        setMfaEnrolled(false)
      } finally {
        if (!cancelled) {
          setMfaChecked(true)
          setNeedsEnrollmentCheck(false)
        }
      }
    }

    checkEnrollment()
    return () => { cancelled = true }
  }, [needsEnrollmentCheck])

  function switchOrg(orgId) {
    const org = orgs.find(o => o.id === orgId)
    if (org) {
      setCurrentOrg(org)
      localStorage.setItem('currentOrgId', orgId)
    }
  }

  async function signUp(email, password, orgName) {
    const resp = await fetch('/api/v1/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, org_name: orgName }),
    })

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.detail || 'Signup failed')
    }

    const result = await resp.json()

    if (result.access_token && result.refresh_token) {
      await supabase.auth.setSession({
        access_token: result.access_token,
        refresh_token: result.refresh_token,
      })
    }

    return result
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return data
  }

  async function signOut() {
    await supabase.auth.signOut()
    setAccessToken(null)
    setUser(null)
    setOrgs([])
    setCurrentOrg(null)
    setMfaEnrolled(false)
    setMfaVerified(false)
    setMfaChecked(false)
    setNeedsEnrollmentCheck(false)
    localStorage.removeItem('currentOrgId')
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        orgs,
        currentOrg,
        loading,
        orgError,
        refreshOrgs: loadOrgs,
        signUp,
        signIn,
        signOut,
        switchOrg,
        isAuthenticated: !!user,
        mfaEnrolled,
        mfaVerified,
        mfaChecked,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
